import { useCallback, useEffect, useRef } from 'react';

// Hook for using Google reCAPTCHA v2 invisible widget without the
// declarative `data-callback="windowGlobalName"` pattern. CQ-21:
// previously each form assigned a uniquely-named global on `window` so
// reCAPTCHA's auto-render could call it. That's fragile (typos,
// collisions if two forms ever mount simultaneously) and pollutes the
// global namespace. This hook calls grecaptcha.render() programmatically
// and passes the verify callback as a closure.
//
// Usage:
//   const { containerRef, execute, reset } = useInvisibleRecaptcha({
//     siteKey: RECAPTCHA_SITE_KEY,
//     onVerify: (token) => submitForm(token),
//   });
//   // In JSX: <div ref={containerRef} />
//   // On submit: execute();
//   // After submit completes: reset();
//
// If `siteKey` is falsy (reCAPTCHA not configured), execute() resolves
// immediately with `null` token so callers can still proceed.
export default function useInvisibleRecaptcha({ siteKey, onVerify, onUnavailable }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onUnavailableRef = useRef(onUnavailable);
  const scriptLoadedRef = useRef(false);

  // Keep the latest onVerify in a ref so the callback grecaptcha holds
  // always invokes the current closure (state captured at submit time).
  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  useEffect(() => {
    onUnavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  const ensureScript = useCallback(() => {
    if (!siteKey) return;
    if (scriptLoadedRef.current) return;
    if (document.querySelector('script[src*="recaptcha/api.js"]')) {
      scriptLoadedRef.current = true;
      return;
    }
    scriptLoadedRef.current = true;
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [siteKey]);

  const renderWidget = useCallback(() => {
    if (widgetIdRef.current !== null) return;
    if (!siteKey) return;
    if (!containerRef.current) return;
    if (!window.grecaptcha || !window.grecaptcha.render) return;
    widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
      sitekey: siteKey,
      size: 'invisible',
      callback: (token) => {
        if (onVerifyRef.current) onVerifyRef.current(token);
      },
      // Without these two the form is a dead end: if the visitor dismisses
      // the challenge or it times out, the verify callback never fires and
      // the submit button sits on "Submitting..." forever.
      'expired-callback': () => {
        if (onUnavailableRef.current) onUnavailableRef.current('expired');
      },
      'error-callback': () => {
        if (onUnavailableRef.current) onUnavailableRef.current('error');
      },
    });
  }, [siteKey]);

  const execute = useCallback(async () => {
    if (!siteKey) {
      // No reCAPTCHA configured — call onVerify with null token so the
      // caller's submit path still runs.
      if (onVerifyRef.current) onVerifyRef.current(null);
      return;
    }
    ensureScript();
    // grecaptcha may not be ready yet on the very first interaction.
    // Poll briefly until the render API is available, then render and
    // execute. The cost is one tight loop on first submit; subsequent
    // submits are immediate.
    const start = Date.now();
    while (!(window.grecaptcha && window.grecaptcha.render) && Date.now() - start < 10000) {
      await new Promise((r) => setTimeout(r, 50));
    }
    renderWidget();
    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.execute(widgetIdRef.current);
    } else if (onUnavailableRef.current || onVerifyRef.current) {
      // grecaptcha never loaded - almost always an ad blocker or privacy
      // extension. Submitting a token-less request would just earn a
      // "Please complete the reCAPTCHA" error pointing at a widget the
      // visitor cannot see, so tell the caller instead so it can offer a
      // way through.
      if (onUnavailableRef.current) onUnavailableRef.current('blocked');
      else onVerifyRef.current(null);
    }
  }, [siteKey, ensureScript, renderWidget]);

  const reset = useCallback(() => {
    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
  }, []);

  return { containerRef, execute, reset, prepare: ensureScript };
}
